import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import type { WeeklyTrendEntry } from '@/hooks/useEvents'
import { fmtDate, fmtAxisDate } from '@/lib/dates'

// Week-over-week trend for multi-week locations (spec View A): revenue per
// event. Sell-through lives in the table and summary cards, so it no longer
// gets its own panel here. Burgundy is identity, not status: events in the
// analysis scope are full --brand, the rest a lighter tint for context.
const BRAND = 'var(--brand)'
const BRAND_SOFT = 'var(--brand-soft)'
const GRID = 'var(--line-warm)'
const MUTE = 'var(--mute)'

const money = (v: number) => `$${v.toLocaleString('en-US', { maximumFractionDigits: 0 })}`

function tooltipStyle() {
  return {
    contentStyle: {
      background: 'var(--surface-card)',
      border: '1px solid var(--line-warm)',
      borderRadius: 2,
      fontSize: 11,
      color: 'var(--text-primary)',
    },
    labelStyle: { color: 'var(--mute)' },
    cursor: { fill: 'var(--surface-header)' },
  }
}

export function WeeklyTrendChart({
  weeks,
  filtersActive = false,
  selectedEventIds,
}: {
  weeks: WeeklyTrendEntry[]
  // Events currently in the analysis scope; their bars are full burgundy.
  // With "All weeks" every event is in scope, so every bar is.
  selectedEventIds: number[]
  // Whole-week totals — the product filter chips narrow the table below, not
  // this. The caption keeps the two from disagreeing silently.
  filtersActive?: boolean
}) {
  if (weeks.length < 2) return null
  const selected = new Set(selectedEventIds)
  const data = weeks.map((w) => ({
    ...w,
    label: fmtAxisDate(w.event_date),
  }))
  const axisProps = {
    tick: { fontSize: 10, fill: MUTE },
    stroke: GRID,
    tickLine: false,
  }
  return (
    <div className="border border-[var(--line-warm)] bg-[var(--surface-card)] p-3 mb-3">
      <h2 className="text-sm font-semibold mb-2">Week-over-week trend</h2>
      <div className="text-xs text-[var(--mute)] mb-1">
        Revenue by event
        {filtersActive && ' · All products — not affected by filters'}
      </div>
      <ResponsiveContainer width="100%" height={160}>
        {/* Top margin leaves room for the value labels above the tallest bar. */}
        <BarChart data={data} margin={{ top: 18, right: 8, left: 0, bottom: 0 }} barCategoryGap="35%">
          <CartesianGrid stroke={GRID} vertical={false} />
          <XAxis dataKey="label" {...axisProps} />
          <YAxis {...axisProps} tickFormatter={money} width={56} />
          <Tooltip
            {...tooltipStyle()}
            formatter={(value) => [money(Number(value)), 'Revenue']}
            labelFormatter={(_, payload) =>
              payload?.[0]
                ? `${fmtDate(payload[0].payload.event_date)} (${payload[0].payload.discipline ?? ''})`
                : ''
            }
          />
          <Bar dataKey="revenue" radius={[4, 4, 0, 0]}>
            {data.map((w) => (
              <Cell key={w.event_id} fill={selected.has(w.event_id) ? BRAND : BRAND_SOFT} />
            ))}
            <LabelList
              dataKey="revenue"
              position="top"
              formatter={(v) => money(Number(v))}
              style={{ fontSize: 10, fill: 'var(--ink-2)' }}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}
